import { useId, useState } from "react";
import { Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatHoursMinutes, parseMinutes } from "@/lib/duration";
import { formatTime } from "../types";
import { reportError } from "./utils";
import { DurationField } from "./DurationField";

export function InlineEstimatedTime({
	value,
	canEdit,
	onSave,
}: {
	value: number;
	canEdit: boolean;
	onSave: (v: number) => Promise<void>;
}) {
	const id = useId();
	const [open, setOpen] = useState(false);
	const [text, setText] = useState("");
	const [saving, setSaving] = useState(false);

	const trimmed = text.trim();
	const minutes = trimmed ? parseMinutes(trimmed) : 0;
	const canSubmit = minutes != null && !saving;

	function onOpenChange(next: boolean) {
		if (next) setText(value ? formatHoursMinutes(value) : "");
		setOpen(next);
	}

	async function commit(next: number) {
		if (next === value) {
			setOpen(false);
			return;
		}
		setSaving(true);
		try {
			await onSave(next);
			setOpen(false);
		} catch (e) {
			reportError(e);
		} finally {
			setSaving(false);
		}
	}

	const display = (
		<>
			<Clock className="h-4 w-4 shrink-0 text-muted" />
			<span className={cn(!value && "text-muted text-xs")}>
				{value ? formatTime(value) : canEdit ? "Set estimate" : "None"}
			</span>
		</>
	);

	if (!canEdit) {
		return (
			<div className="flex items-center gap-2 text-sm text-foreground">
				{display}
			</div>
		);
	}

	return (
		<Popover open={open} onOpenChange={onOpenChange}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="-mx-1 flex w-full items-center gap-2 rounded px-1 py-0.5 text-left text-sm text-foreground transition-colors hover:bg-muted-subtle/60"
				>
					{display}
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				className="w-80 p-3"
				onEscapeKeyDown={(e) => e.stopPropagation()}
			>
				<form
					className="flex flex-col gap-3"
					onSubmit={(e) => {
						e.preventDefault();
						if (canSubmit) commit(minutes);
					}}
				>
					<label
						htmlFor={`${id}-est`}
						className="text-sm font-medium text-foreground"
					>
						Estimate
					</label>
					<DurationField
						id={`${id}-est`}
						autoFocus
						text={text}
						onTextChange={setText}
						disabled={saving}
						emptyHint="No estimate. Enter minutes or 1h 30m."
					/>
					<div className="flex items-center justify-end gap-2">
						{value > 0 && (
							<Button
								type="button"
								size="sm"
								variant="destructive_outline"
								className="mr-auto"
								disabled={saving}
								onClick={() => commit(0)}
							>
								Remove
							</Button>
						)}
						<Button
							type="button"
							size="sm"
							variant="ghost"
							disabled={saving}
							onClick={() => setOpen(false)}
						>
							Cancel
						</Button>
						<Button type="submit" size="sm" disabled={!canSubmit}>
							{saving && (
								<Loader2 className="h-3 w-3 animate-spin" />
							)}
							Save
						</Button>
					</div>
				</form>
			</PopoverContent>
		</Popover>
	);
}
